
import React, { useState, useRef } from 'react';
import { Button } from './Button';
import { Copy, Edit2, X, Clock, CheckCircle2, AlertTriangle, Hash, ArrowRight, Unlock, Building2, Timer, ArrowLeft, PlayCircle, Flag, History, Loader2, Eye } from 'lucide-react';
import { ServiceItem, ProjectGlobalData } from '../types';
import { READ_ONLY_AUSWEG, READ_ONLY_HINWEIS, readOnlyTitle } from '../services/readOnlyNotice';
import { heuteBerlin } from '../services/dateService';

interface Props {
    isOpen: boolean;
    projectName: string;
    inspectionNumber: number;
    dbCurrentNr?: number;
    dbLastExportedNr?: number;
    nextDate: string;
    nextTime: string;
    services?: ServiceItem[];
    globalData?: ProjectGlobalData;
    onDateChange: (date: string) => void;
    onTimeChange: (time: string) => void;
    /**
     * Darf asynchron sein (Archiv-Snapshot, Nummernkreis-Update in der DB) —
     * der Dialog wartet darauf und bleibt so lange gesperrt.
     */
    onDecision: (mode: 'edit' | 'next', billingData?: { serviceId: string, quantity: number, sectionId?: string }, dateOverride?: string, timeOverride?: string) => void | Promise<void>;
    onClose: () => void;
    activeSectionId?: string | null;
    isExported?: boolean;
    currentProjectNumber?: string;
    /**
     * Schreibgeschuetzt geoeffnetes Projekt (Admin → Offene Positionen).
     * Der Riegel gegen das Anlegen einer Begehung sitzt in useReportActions
     * ('next'), greift dort aber erst nach dem Klick. Hier wird der Weg schon
     * vorher sichtbar zugemacht — ansehen ('edit') bleibt offen, das ist der
     * einzige Weg, die Begehung ueberhaupt zu sehen.
     */
    isReadOnly?: boolean;
}

export const LoadReportDialog: React.FC<Props> = ({ 
    isOpen, projectName, inspectionNumber, dbCurrentNr, dbLastExportedNr,
    nextDate, nextTime, globalData,
    onDateChange, onTimeChange, onDecision, onClose, activeSectionId, isExported,
    isReadOnly = false
}) => {
    // Der Dialog wird nur gemountet, solange er offen ist – der Initial-State ist damit der Reset pro Öffnen.
    const [selectedSectionId, setSelectedSectionId] = useState<string>(() => {
        if (activeSectionId && activeSectionId !== 'all') return activeSectionId;
        if (globalData?.enableSections && globalData.sections && globalData.sections.length > 0) return globalData.sections[0].id;
        return '';
    });
    const [forceUnlock, setForceUnlock] = useState(false);
    
    // UI State for Two-Step Process
    const [step, setStep] = useState<'selection' | 'confirmation'>('selection');
    const [pendingMode, setPendingMode] = useState<'edit' | 'next' | null>(null);
    // "Bericht final anlegen" erhöht die Berichtsnummer und schreibt vorher
    // einen Archiv-Snapshot — das dauert. Ohne Sperre bleibt der Button
    // klickbar und ein zweiter Klick erzeugt eine zweite Nummernerhöhung.
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);

    // Normalize Counters
    const currentNrVal = Math.max(dbCurrentNr || 0, inspectionNumber || 0);
    const lastExportedVal = dbLastExportedNr ?? (isExported ? inspectionNumber : Math.max(0, inspectionNumber - 1));

    // LOGIC STATES
    const isZero = currentNrVal === 0;
    const hasOpenDraft = currentNrVal > lastExportedVal;
    const isReadyForNext = currentNrVal === lastExportedVal && !isZero;

    const selectedSection = globalData?.enableSections && selectedSectionId
        ? globalData.sections?.find(s => s.id === selectedSectionId)
        : undefined;
    const calculatedNextNr = selectedSection ? (selectedSection.currentInspectionNumber || 0) + 1 : currentNrVal + 1;

    if (!isOpen) return null;

    const initiateAction = (mode: 'edit' | 'next') => {
        if (mode === 'next') {
            if (globalData?.enableSections && !selectedSectionId) {
                alert("Bitte wählen Sie einen Abschnitt.");
                return;
            }
            // Neuer Bericht: heute — als Kalendertag in Berlin. Der UTC-Tag
            // (toISOString) war zwischen 00:00 und 02:00 noch gestern.
            onDateChange(heuteBerlin());
        }
        setPendingMode(mode);
        setStep('confirmation');
    };

    const handleFinalConfirm = async () => {
        if (!pendingMode) return;
        // Ref-Lock zuerst: der State greift erst nach dem Re-Render.
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        try {
            await onDecision(pendingMode, {
                serviceId: '',
                quantity: 1,
                sectionId: pendingMode === 'next' ? (selectedSectionId || undefined) : undefined
            }, nextDate, nextTime);
            // Kein Zurücksetzen im Erfolgsfall: der Aufrufer schließt den Dialog,
            // bis dahin bleibt der Button gesperrt.
        } catch (e) {
            console.error('Berichts-Aktion fehlgeschlagen', e);
            alert('Der Vorgang konnte nicht abgeschlossen werden. Bitte erneut versuchen.');
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <div data-glass-view="projekt-dialog" className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-scale-in">
            <div className="bg-slate-900 text-white p-5 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <Hash className="w-6 h-6 text-blue-400"/> 
                    <div>
                        <h3 className="text-lg font-bold">
                            {step === 'selection' ? 'Berichts-Management' : (pendingMode === 'edit' ? 'Entwurf bearbeiten' : 'Begehung anlegen')}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[11px] uppercase font-bold text-slate-400 flex items-center gap-1">
                                <Building2 className="w-3 h-3" /> {projectName}
                            </span>
                        </div>
                    </div>
                </div>
                <button onClick={onClose} disabled={isSubmitting} className="hover:bg-white/10 p-1 rounded transition-colors text-white/80 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed">
                    <X className="w-5 h-5" />
                </button>
            </div>
            
            <div className="p-6 space-y-6">
                
                {step === 'selection' && (
                    <>
                        {/* STATUS COUNTERS */}
                        <div data-tutorial="begehung.zaehler" className="bg-slate-50 border border-slate-200 p-4 rounded-xl flex justify-between items-center shadow-inner">
                            <div className="flex gap-4">
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Zähler</span>
                                    <span className="font-mono font-bold text-slate-700">#{currentNrVal}</span>
                                </div>
                                <div className="w-px h-8 bg-slate-200"></div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Exportiert</span>
                                    <span className="font-mono font-bold text-slate-700">#{lastExportedVal}</span>
                                </div>
                            </div>
                            
                            <div className={`px-3 py-1 rounded-full text-[11px] font-bold border uppercase tracking-wider flex items-center gap-1.5 
                                ${isZero 
                                    ? 'bg-blue-100 text-blue-700 border-blue-200' 
                                    : (isReadyForNext 
                                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                                        : 'bg-orange-100 text-orange-700 border-orange-200')
                                }`}>
                                
                                {isZero ? (
                                    <>
                                        <Flag className="w-3 h-3" />
                                        <span>Neues Projekt</span>
                                    </>
                                ) : isReadyForNext ? (
                                    <>
                                        <CheckCircle2 className="w-3 h-3"/>
                                        <span>Bereit für Folgebericht</span>
                                    </>
                                ) : (
                                    <>
                                        <Timer className="w-3 h-3 animate-pulse"/>
                                        <span>Entwurf aktiv</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4">
                            {/* ACTION 1: EDIT CURRENT (Disabled if Zero or if no Draft and user prefers specific path) */}
                            <button 
                                disabled={isZero}
                                onClick={() => initiateAction('edit')} 
                                className={`w-full text-left p-4 rounded-xl border-2 shadow-sm transition-all group flex items-center justify-between 
                                    ${isZero 
                                        ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed grayscale' 
                                        : (hasOpenDraft)
                                            ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700 hover:shadow-md' 
                                            : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600'
                                    }
                                `}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-lg ${isZero ? 'bg-slate-100' : (hasOpenDraft ? 'bg-white/20' : 'bg-blue-50 text-blue-600')}`}>
                                        {hasOpenDraft ? <Edit2 className="w-5 h-5"/> : <History className="w-5 h-5"/>}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-sm">
                                            {isZero ? 'Kein Entwurf vorhanden' : (isReadyForNext ? `Bericht #${currentNrVal} (Archiv) öffnen` : `Entwurf #${currentNrVal} bearbeiten`)}
                                        </h4>
                                        <p className={`text-xs mt-0.5 ${hasOpenDraft ? 'text-blue-100' : 'text-slate-400'}`}>
                                            {isZero ? 'Legen Sie zuerst die erste Begehung an.' : (hasOpenDraft ? 'Diesen Bericht vervollständigen.' : 'Gesendeten Bericht ansehen.')}
                                        </p>
                                    </div>
                                </div>
                                {!isZero && <ArrowRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${hasOpenDraft ? 'text-white' : 'text-slate-300 group-hover:text-blue-500'}`} />}
                            </button>

                            <div className="relative py-2">
                                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                                <div className="relative flex justify-center"><span className="bg-white px-2 text-[11px] text-slate-400 uppercase font-black tracking-widest">Oder</span></div>
                            </div>

                            {/* ACTION 2: NEXT REPORT */}
                            <div data-tutorial="begehung.folgebericht" className={`p-5 rounded-2xl border-2 transition-all shadow-sm ${(!hasOpenDraft || forceUnlock) ? 'border-emerald-200 bg-white' : 'border-slate-100 bg-slate-50/80 opacity-90'}`}>
                                <div className="flex justify-between items-center mb-5">
                                    <h4 className={`font-bold text-sm flex items-center gap-2 ${(!hasOpenDraft || forceUnlock) ? 'text-emerald-800' : 'text-slate-400'}`}>
                                        <Copy className="w-4 h-4"/> {isZero ? 'Ersten Bericht #1 anlegen' : `Folgebericht #${calculatedNextNr} anlegen`}
                                    </h4>
                                    {hasOpenDraft && !forceUnlock && (
                                        <button 
                                            onClick={() => setForceUnlock(true)}
                                            className="text-[11px] font-bold text-orange-600 bg-white border border-orange-200 px-3 py-1.5 rounded-lg hover:bg-orange-50 transition-colors flex items-center gap-2 shadow-sm"
                                        >
                                            <Unlock className="w-3 h-3" /> Sperre lösen
                                        </button>
                                    )}
                                </div>
                                
                                <div className="space-y-5">
                                    {hasOpenDraft && !forceUnlock ? (
                                        <div data-tutorial="begehung.folgebericht-gesperrt" className="bg-orange-50 border border-orange-100 rounded-xl p-3 flex gap-3 items-start animate-fade-in">
                                            <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                                            <p className="text-[11px] text-orange-800 leading-relaxed">
                                                Der Folgebericht ist gesperrt, da der aktuelle Bericht noch nicht exportiert wurde. Bitte bearbeiten und versenden Sie Bericht #{currentNrVal} zuerst.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-5 animate-slide-up">
                                            {globalData?.enableSections && (
                                                <div className="space-y-1.5">
                                                     <label className="text-[11px] text-slate-500 uppercase font-black tracking-wider">Abschnitt wählen</label>
                                                     <select 
                                                        value={selectedSectionId} 
                                                        onChange={(e) => setSelectedSectionId(e.target.value)}
                                                        className="w-full text-xs border border-slate-300 rounded-xl px-3 py-2.5 outline-none font-bold bg-white text-slate-700"
                                                     >
                                                        {globalData.sections?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                     </select>
                                                </div>
                                            )}

                                            {isReadOnly && (
                                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex gap-3 items-start">
                                                    <Eye className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                                                    <p className="text-[11px] text-slate-600 leading-relaxed">
                                                        {READ_ONLY_HINWEIS} {READ_ONLY_AUSWEG}
                                                    </p>
                                                </div>
                                            )}

                                            <Button
                                                data-tutorial="begehung.neuer-bericht"
                                                variant="primary"
                                                onClick={() => initiateAction('next')}
                                                disabled={isReadOnly}
                                                title={isReadOnly ? readOnlyTitle('Es wird keine neue Begehung angelegt und nichts archiviert.') : undefined}
                                                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-100 text-base font-black rounded-xl"
                                            >
                                                {isZero ? 'Bericht #1 jetzt erstellen' : `Bericht #${calculatedNextNr} jetzt erstellen`}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {step === 'confirmation' && (
                    <div className="space-y-6 animate-slide-in">
                        <div className="bg-blue-50 border border-blue-100 p-6 rounded-2xl space-y-6 shadow-sm">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="bg-white p-2 rounded-lg text-blue-600 shadow-sm">
                                    <Clock className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-blue-900 text-sm">Zeitstempel festlegen</h4>
                                    <p className="text-xs text-blue-700 opacity-80">Wann fand die Begehung statt?</p>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <label className="text-[11px] text-slate-500 uppercase font-bold ml-1">Datum der Begehung</label>
                                    <input 
                                        data-tutorial="begehung.datum"
                                        type="date" 
                                        value={nextDate} 
                                        onChange={(e) => onDateChange(e.target.value)} 
                                        className="w-full text-base font-bold border border-slate-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none bg-white transition-all shadow-sm"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[11px] text-slate-500 uppercase font-bold ml-1">Uhrzeit</label>
                                    <input 
                                        data-tutorial="begehung.uhrzeit"
                                        type="time" 
                                        value={nextTime} 
                                        onChange={(e) => onTimeChange(e.target.value)} 
                                        className="w-full text-base font-bold border border-slate-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none bg-white transition-all shadow-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setStep('selection')}
                                disabled={isSubmitting}
                                className="px-4 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ArrowLeft className="w-4 h-4" /> Zurück
                            </button>
                            <Button
                                data-tutorial="begehung.anlegen-bestaetigen"
                                variant="primary"
                                onClick={handleFinalConfirm}
                                disabled={isSubmitting}
                                className="flex-1 py-3 text-sm font-black rounded-xl shadow-lg"
                                icon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                            >
                                {isSubmitting
                                    ? 'Wird angelegt…'
                                    : (pendingMode === 'edit' ? 'Bearbeitung fortsetzen' : 'Bericht final anlegen')}
                            </Button>
                        </div>
                    </div>
                )}

            </div>
            
            {step === 'selection' && (
                <div className="p-4 bg-slate-50 border-t border-slate-100 text-center">
                    <p className="text-[11px] text-slate-400 font-medium italic">Abrechnung und Export erfolgen im Berichtseditor.</p>
                </div>
            )}
          </div>
        </div>
    );
};
